import { useLabelColumnClearance, type ScrollerResolver } from "@/hooks/useHorizontalScrollOffset";

export interface TimelineHourLabelsProps {
  /** Hour the timeline's left edge represents. */
  startHour: number;
  /** How many hour labels to draw (the timeline's span in hours). */
  hours: number;
  /** Width of one hour in px, zoom already applied. */
  pixelsPerHour: number;
  /** Renders "09:00" honouring the producer's 12h/24h setting. */
  formatTime: (time: string) => string;
  /** The element that scrolls, so a label can hide before it reaches the labels. */
  resolveScroller: ScrollerResolver;
  /** Padding between that scroll container and the grid (read-only canvas: p-4). */
  canvasPadding?: number;
  /** Per-view styling of a single label. */
  className: string;
  /** Per-view vertical offset of a single label. */
  top?: number;
}

/**
 * The hour axis of a horizontal schedule timeline.
 *
 * Each label is centred on its grid line, so half of it hangs to the left —
 * over the sticky stage / custom-subject label column for the first hour, and
 * for every hour the producer scrolls past. Nothing may show up there, so a
 * label stays hidden until it is fully clear of that column.
 *
 * Its own leaf component on purpose: it subscribes to the scroll offset, and
 * that value changes on every frame while dragging. Keeping the subscription
 * here means a scroll re-renders 24 spans instead of the whole schedule.
 */
export function TimelineHourLabels({
  startHour,
  hours,
  pixelsPerHour,
  formatTime,
  resolveScroller,
  canvasPadding = 0,
  className,
  top,
}: TimelineHourLabelsProps) {
  const firstClearOffset = useLabelColumnClearance(resolveScroller, canvasPadding);

  return (
    <>
      {Array.from({ length: hours }, (_, i) => {
        const offset = i * pixelsPerHour;
        if (offset < firstClearOffset) return null;
        const displayHour = (startHour + i) % 24;
        return (
          <span
            key={i}
            className={className}
            style={{ left: `${offset}px`, top }}
            data-testid="timeline-hour-label"
          >
            {formatTime(`${String(displayHour).padStart(2, "0")}:00`)}
          </span>
        );
      })}
    </>
  );
}
